import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import customFetch from '@utils/customFetch';
import { useDashboardContext } from '@pages/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  EyeIcon,
  CursorArrowRaysIcon,
  ArrowDownTrayIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';
import TimeSeriesChart from '../statistiken/TimeSeriesChart';
import Loading from '../../Loading';

const periods = [
  { label: 'Letzte 7 Tage', value: '7' },
  { label: 'Letzte 30 Tage', value: '30' },
  { label: 'Letzte 90 Tage', value: '90' },
  { label: 'Letztes Jahr', value: '365' },
];

const companyStatisticsQuery = (companyId, days) => ({
  queryKey: ['companyStatistics', companyId, days],
  queryFn: async () => {
    const [overview, perUser, timeSeries] = await Promise.all([
      customFetch.get(`/companies/${companyId}/analytics/overview?days=${days}`),
      customFetch.get(`/companies/${companyId}/analytics/per-user?days=${days}`),
      customFetch.get(
        `/companies/${companyId}/analytics/timeseries?days=${days}&interval=day`
      ),
    ]);
    return {
      overview: overview.data.overview,
      previousPeriod: overview.data.previousPeriod,
      users: perUser.data.users,
      timeSeries: timeSeries.data.timeSeriesData,
    };
  },
});

export const loader = (queryClient) => async () => {
  try {
    const userData = await queryClient.ensureQueryData({
      queryKey: ['user'],
      queryFn: async () => {
        const { data } = await customFetch.get('/users/current-user');
        return data;
      },
    });
    const companyId = userData.user.company;
    if (!companyId) return {};
    await queryClient.ensureQueryData(companyStatisticsQuery(companyId, 30));
    return { companyId };
  } catch (error) {
    return {};
  }
};

// Prozentuale Veränderung gegenüber der Vorperiode.
const trendOf = (current, previous) => {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

const TrendBadge = ({ trend }) => {
  if (trend === null || trend === undefined) {
    return (
      <span className="inline-flex items-center text-xs text-gray-400">
        <MinusIcon className="h-3 w-3 mr-0.5" />
        –
      </span>
    );
  }
  const up = trend >= 0;
  const Icon = up ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  return (
    <span
      className={`inline-flex items-center text-xs font-medium ${
        up ? 'text-green-600' : 'text-red-600'
      }`}
    >
      <Icon className="h-3 w-3 mr-0.5" />
      {up ? '+' : ''}
      {trend.toFixed(0)}%
    </span>
  );
};

const OverviewCards = ({ overview, previousPeriod }) => {
  const stats = [
    {
      name: 'Profilaufrufe',
      key: 'pageViews',
      icon: EyeIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Link-Klicks',
      key: 'linkClicks',
      icon: CursorArrowRaysIcon,
      color: 'bg-svisit-primary-600',
    },
    {
      name: 'Profil-Downloads',
      key: 'downloads',
      icon: ArrowDownTrayIcon,
      color: 'bg-purple-500',
    },
    {
      name: 'Unique Besucher',
      key: 'uniqueVisitors',
      icon: UserGroupIcon,
      color: 'bg-indigo-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat) => {
        const value = overview?.[stat.key] || 0;
        const trend = trendOf(value, previousPeriod?.[stat.key]);
        return (
          <Card key={stat.name}>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className={`${stat.color} rounded-md p-3`}>
                  <stat.icon
                    className="h-6 w-6 text-white"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dt className="truncate text-sm font-medium text-gray-500">
                    {stat.name}
                  </dt>
                  <dd className="flex items-baseline gap-2">
                    <span className="text-2xl font-semibold text-gray-900">
                      {value.toLocaleString('de-CH')}
                    </span>
                    <TrendBadge trend={trend} />
                  </dd>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const sortableColumns = [
  { key: 'pageViews', label: 'Aufrufe' },
  { key: 'linkClicks', label: 'Klicks' },
  { key: 'downloads', label: 'Downloads' },
  { key: 'uniqueVisitors', label: 'Besucher' },
  { key: 'clickRate', label: 'Klickrate' },
];

const formatLastActivity = (value) => {
  if (!value) return '–';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '–';
  return date.toLocaleDateString('de-CH');
};

const Leaderboard = ({ users }) => {
  const [sortBy, setSortBy] = useState('pageViews');

  const sorted = useMemo(() => {
    const rows = [...users].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
    return rows.map((row, index) => ({ ...row, rank: index + 1 }));
  }, [users, sortBy]);

  const medalColor = (rank) => {
    if (rank === 1) return 'text-yellow-500';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-amber-700';
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team-Leistung</CardTitle>
        <p className="text-sm text-gray-500">
          Profilaktivität pro Mitarbeiter im gewählten Zeitraum.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="py-3 pl-2 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  #
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Mitarbeiter
                </th>
                {sortableColumns.map((col) => (
                  <th
                    key={col.key}
                    className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide"
                  >
                    <button
                      type="button"
                      onClick={() => setSortBy(col.key)}
                      className={`inline-flex items-center gap-1 ${
                        sortBy === col.key
                          ? 'text-svisit-primary-600'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {col.label}
                      {sortBy === col.key && <span aria-hidden>▾</span>}
                    </button>
                  </th>
                ))}
                <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Letzte Aktivität
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sorted.map((row) => (
                <tr
                  key={row.userId}
                  className={row.rank <= 3 ? 'bg-gray-50/60' : undefined}
                >
                  <td className="whitespace-nowrap py-3 pl-2 pr-3 text-sm text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      {medalColor(row.rank) ? (
                        <TrophyIcon
                          className={`h-4 w-4 ${medalColor(row.rank)}`}
                        />
                      ) : null}
                      {row.rank}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-sm">
                    <div className="flex items-center gap-3">
                      {row.avatar || row.avatarUrl ? (
                        <img
                          src={row.avatarUrl || row.avatar}
                          alt=""
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600">
                          {(row.firstName?.[0] || '') + (row.lastName?.[0] || '')}
                        </span>
                      )}
                      <div>
                        <div className="font-medium text-gray-900">
                          {row.firstName} {row.lastName}
                        </div>
                        {row.title && (
                          <div className="text-xs text-gray-500">{row.title}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-900">
                    {row.pageViews.toLocaleString('de-CH')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-900">
                    {row.linkClicks.toLocaleString('de-CH')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-900">
                    {row.downloads.toLocaleString('de-CH')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-900">
                    {row.uniqueVisitors.toLocaleString('de-CH')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-900">
                    {row.clickRate.toLocaleString('de-CH')}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-500">
                    {formatLastActivity(row.lastActivity)}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="py-8 text-center text-sm text-gray-500"
                  >
                    Noch keine Mitarbeiter in der Firma.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const CompanyStatistics = () => {
  const { user } = useDashboardContext();
  const companyId = user.company;
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  const { data, isLoading, isError, error } = useQuery({
    ...companyStatisticsQuery(companyId, selectedPeriod),
    enabled: !!companyId,
  });

  if (!companyId) {
    return (
      <div className="p-4">
        <p className="text-gray-600">Keine Firma zugeordnet.</p>
      </div>
    );
  }

  if (isLoading) return <Loading />;

  if (isError) {
    return (
      <div className="p-4">
        <h2 className="text-xl font-semibold text-red-600">
          Fehler beim Laden der Firmen-Statistiken
        </h2>
        <p className="text-gray-600">
          {error?.response?.data?.msg || error?.message || 'Ein Fehler ist aufgetreten'}
        </p>
      </div>
    );
  }

  const currentPeriod = periods.find(
    (p) => p.value === String(selectedPeriod)
  );

  return (
    <div className="space-y-6 bg-white px-6 py-6 lg:px-8">
      <div className="border-b border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between">
        <div>
          <h3 className="text-2xl font-semibold leading-6 text-gray-900">
            Firmen-Statistiken
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Konsolidierte Profilaktivität deines Teams und Top-Performer.
          </p>
        </div>
        <div className="mt-3 sm:ml-4 sm:mt-0">
          <Select
            value={String(selectedPeriod)}
            onValueChange={(value) => setSelectedPeriod(Number(value))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={currentPeriod?.label} />
            </SelectTrigger>
            <SelectContent>
              {periods.map((period) => (
                <SelectItem key={period.value} value={period.value}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <OverviewCards
        overview={data?.overview}
        previousPeriod={data?.previousPeriod}
      />

      <TimeSeriesChart data={data?.timeSeries || []} />

      <Leaderboard users={data?.users || []} />
    </div>
  );
};

export default CompanyStatistics;
